import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/common/apiClient";
import { toast } from "sonner";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";

interface UserKPIForm {
  _id: string;
  kpiFormTitle: string;
  userName: string;
  userEmail: string;
  status: string;
  reviewStatus: string | null;
  submittedAt: string | null;
  reviewFinalizedAt: string | null;
  reviewFinalizedByName: string | null;
}

export const ViewAllUserKPIForm = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState<UserKPIForm[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "completed">("all");

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await apiClient.get("/api/user-kpi-form");
      if (response.success) {
        // const submittedForms = (response.data || []).filter(
        //   (f: UserKPIForm) => f.status === "submitted"
        // );
        setForms(response.data || []);
      } else {
        toast.error("Failed to load forms");
      }
    } catch (error) {
      toast.error("Failed to load forms");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReview = (formId: string) => {
    navigate(`/review-kpi-form/${formId}`);
  };

  const getFilteredForms = () => {
    if (filter === "pending") {
      return forms.filter((f) => f.reviewStatus === "pending");
    }
    if (filter === "completed") {
      return forms.filter((f) => f.reviewStatus === "completed");
    }
    return forms;
  };

  const getReviewStatusBadge = (reviewStatus: string | null) => {
    if (reviewStatus === "completed") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Completed
        </span>
      );
    }
    if (reviewStatus === "pending") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Pending Review
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        No Status
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  const filteredForms = getFilteredForms();

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Review KPI Forms</h1>
        <p className="text-sm text-gray-500 mt-1">
          View and review submitted KPI forms
        </p>
      </div>

      <div className="mb-4 flex gap-2">
        <Button
          variant={filter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("all")}
        >
          All ({forms.length})
        </Button>
        <Button
          variant={filter === "pending" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("pending")}
        >
          Pending ({forms.filter((f) => f.reviewStatus === "pending").length})
        </Button>
        <Button
          variant={filter === "completed" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("completed")}
        >
          Completed (
          {forms.filter((f) => f.reviewStatus === "completed").length})
        </Button>
      </div>

      <div className="bg-white rounded-lg border shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Form Title</TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Submitted Date</TableHead>
              <TableHead>Review Status</TableHead>
              <TableHead>Finalized By</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredForms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-gray-500">
                  No forms found
                </TableCell>
              </TableRow>
            ) : (
              filteredForms.map((form) => (
                <TableRow key={form._id}>
                  <TableCell className="font-medium">
                    {form.kpiFormTitle}
                  </TableCell>
                  <TableCell>{form.userName}</TableCell>
                  <TableCell>{form.userEmail}</TableCell>
                  <TableCell>{formatDate(form.submittedAt)}</TableCell>
                  <TableCell>
                    {getReviewStatusBadge(form.reviewStatus)}
                  </TableCell>
                  <TableCell>{form.reviewFinalizedByName || "-"}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReview(form._id)}
                    >
                      {form.reviewStatus === "completed" ? "View" : "Review"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
